import React, { useEffect, useState } from 'react';
import {
  Grid,
  Container,
  makeStyles,
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Hidden,
  CircularProgress,
} from '@material-ui/core';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import CardMedia from '@material-ui/core/CardMedia';

import { withTranslation } from 'config/i18n';
import useHandleResponse from 'utils/useHandleResponse';
import axios, { API } from 'config/axios';
import {
  useLayoutSnackbar,
  useLayoutLoadingSpinner,
  useLayoutState,
} from 'components/shared/Layout';
import Autocomplete from '@material-ui/lab/Autocomplete';
import CachedIcon from '@material-ui/icons/Cached';
import VerifiedUserIcon from '@material-ui/icons/VerifiedUser';
import VerifyPhone from 'components/IdentityVerification/VerifyPhone';
import { countries } from 'data/countries';
import InfoIcon from '@material-ui/icons/Info';
import FormErrors from 'components/shared/FormErrors';
import InternationalPhone from 'common/components/InternationalPhone/InternationalPhone';

const useStyles = makeStyles((theme) => ({
  verificationBox: {
    height: '100%',
  },
  root: {
    display: 'flex',
    boxShadow: 'none',
    height: '100%',
  },
  phoneNote: {
    color: theme.palette.grey[300],
    display: 'flex',
    alignItems: 'center',
  },
  infoIcon: {
    color: theme.palette.grey[300],
    width: theme.spacing(2),
    height: theme.spacing(2),
    marginRight: theme.spacing(1),
  },
  verify: {
    display: 'flex',
    lineHeight: theme.typography.pxToRem(25),
    '& svg': {
      color: theme.palette.success.main,
      marginRight: theme.spacing(0.5),
    },
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    counterReset: 'section',
  },
  cover: {
    width: '30%',
    backgroundPosition: 'top center',
  },
  paper: {
    padding: theme.spacing(3, 2, 0, 2),
    width: '100%',
    margin: theme.spacing(4, 0),
    display: 'flex',
  },
  count: { width: 75 },
  countentWraper: {
    flexGrow: 1,
  },
  num: {
    '&::before': {
      marginTop: theme.spacing(0.5),
      counterIncrement: 'section',
      content: 'counter(section)',
      border: `2px solid ${theme.palette.primary.main}`,
      display: 'inline-flex',
      width: 40,
      height: 40,
      borderRadius: '50%',
      justifyContent: 'center',
      alignItems: 'center',
    },
  },
  emailVerification: {
    display: 'flex',
    alignItems: 'center',
  },
  numMobile: {
    '&::before': {
      marginRight: theme.spacing(1),
      counterIncrement: 'section',
      width: 30,
      height: 30,
    },
  },
  resendButton: {
    textDecoration: 'underline',
  },
}));

const IdentityVerification = ({
  t,
  data,
  setData,
  submited,
  loaded,
  continueBtnAtion,
  timezoneErrs,
}) => {
  const classes = useStyles();
  const { layoutState } = useLayoutState();
  const [, setLoading] = useLayoutLoadingSpinner();
  // login, register, previously_associated, has_been_associated
  const [verifyTimezone, setVerifyTimezone] = useState(false);
  const [snackbar, setSnackbar] = useLayoutSnackbar();
  const { handleError } = useHandleResponse();
  const currentPhoneCode = countries.filter((item) => item.code == data.country_code)[0]?.phone;
  const [inputPhoneValues, setInputPhoneValues] = useState({
    value: data.phone_code && data.phone ? `${data.phone_code}${data.phone}` : '',
  });

  const handleInputChange = (e) => {
    setData({
      ...data,
      [e.target.name]: e.target.value,
    });
  };
  const handlephoneNumberChange = (phoneData) => {
    setInputPhoneValues(phoneData);
  };

  const handlePhoneNumberBlur = (phoneData) => {
    setData((prev) => ({
      ...prev,
      phone: phoneData.number.replace(phoneData.phoneCode, ''),
      phone_code: phoneData.phoneCode,
      country_code: phoneData.countryCode,
      status: phoneData.status,
    }));
  };

  const verifyPhoneNumberBtnClick = () => {
    const params = {
      value: inputPhoneValues.value,
      number: inputPhoneValues.number.replace(/-|\+|\(|\)| /g, ''),
      countryCode: inputPhoneValues.countryCode,
      phoneCode: inputPhoneValues.phoneCode,
    };

    if (inputPhoneValues.status) {
      handlePhoneNumberBlur({
        ...params,
        status: inputPhoneValues.status,
      });
      return { ...params, status: inputPhoneValues.status };
    }
    if (inputPhoneValues.value) {
      handlePhoneNumberBlur({
        ...params,
        status: false,
      });
      return { status: false };
    }
    handlePhoneNumberBlur({
      ...params,
      status: null,
    });
    return { status: null };
  };

  const sendEmail = () => {
    setLoading(true);
    axios
      .post(API.users.sendEmail, {
        authenticity_token: layoutState.authenticity_token,
      })
      .then(() => {
        setSnackbar({
          ...snackbar,
          open: true,
          severity: 'success',
          message: t('fe_es_identity_verification:email_sent'),
        });
      })
      .catch((error) => {
        handleError(error);
      })
      .then(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (process.browser && data.time_zone) {
      setVerifyTimezone(true);
    }
  }, []);
  useEffect(() => {
    if (data && data.phone_code == null) {
      setData({
        ...data,
        phone_code: currentPhoneCode,
        phone: data.phone && data.phone.toString().replace(`+${currentPhoneCode}`, ''),
      });
    }
  }, []);

  return (
    <>
      <Box className={classes.verificationBox}>
        <Card className={classes.root}>
          <Hidden mdDown>
            <CardMedia
              className={classes.cover}
              image="/imgs/side_banner.jpg"
              title="EagleRider Motorcycles"
            />
          </Hidden>

          <CardContent className={classes.content}>
            {!loaded ? (
              <Box display="flex" justifyContent="center" py={15}>
                <CircularProgress />
              </Box>
            ) : (
              <Container disableGutters>
                <Typography component="h5" variant="h3" data-testid="es-checkin-complete-account-heading">
                  {t('fe_es_identity_verification:complete_account')}
                </Typography>
                <Typography variant="subtitle1" color="textSecondary">
                  {t('fe_es_identity_verification:complete_account_subheading')}
                </Typography>

                <Wraper title={t('fe_es_identity_verification:email')}>
                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                      <TextField
                        variant="outlined"
                        error={submited && !data.email_verified}
                        helperText={
                          !data.email_verified ? t('fe_es_identity_verification:email_sent') : false
                        }
                        onChange={handleInputChange}
                        disabled
                        label={t('fe_es_identity_verification:email')}
                        name="email"
                        fullWidth
                        value={data.email || ''}
                      />
                    </Grid>

                      <Grid item md={12} className={classes.emailVerification}>
                      {!data.email_verified ? (
                        <>
                          {t('fe_es_identity_verification:did_not_get_email')}{' '}
                            <Button
                              onClick={sendEmail}
                              color="default"
                              endIcon={<CachedIcon />}
                              className={classes.resendButton}
                            >
                            {t('fe_es_identity_verification:send_again')}
                          </Button>
                        </>
                      ) : (
                        <Typography
                          className={classes.verify}
                          variant="body2"
                          color="textSecondary"
                        >
                              <VerifiedUserIcon color="primary" />
                          <span>{t('fe_es_identity_verification:verified_email')}</span>
                        </Typography>
                      )}
                    </Grid>
                  </Grid>
                </Wraper>

                <Wraper title={t('fe_es_identity_verification:phone_number')}>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <Typography variant="body2" color="textSecondary">
                        {t('fe_es_identity_verification:valid_number')}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <InternationalPhone
                        data={{
                          phone: inputPhoneValues.value,
                          country_code: data.country_code,
                          disabled: data.phone_verified,
                          validPhone: data.status,
                        }}
                        handleChange={handlephoneNumberChange}
                        inputDataTestId="es-checkin-complete-account-phone"
                      />
                    </Grid>
                    <Grid item md={12}>
                      <VerifyPhone
                        setData={setData}
                        data={data}
                        i18n={t}
                        phoneDisplayValue={inputPhoneValues.value}
                        verifyPhoneNumberBtnClick={verifyPhoneNumberBtnClick}
                      />
                      {!data.phone_verified && (
                        <Box mt={2}>
                          <Typography className={classes.phoneNote} variant="body2">
                            <InfoIcon className={classes.infoIcon} />
                            {t('fe_es_identity_verification:will_text_you')}
                          </Typography>
                        </Box>
                      )}
                    </Grid>
                  </Grid>
                </Wraper>

                {data.time_zones && (
                  <Wraper title={t('fe_es_identity_verification:time_zone')}>
                    <Grid container spacing={3}>
                      <Grid item xs={12} md={6}>
                        <Autocomplete
                          id="verify-time-zone"
                          options={data.time_zones}
                          getOptionLabel={(option) => option.key || ''}
                          getOptionSelected={(option, value) => option.key == value.key}
                          value={
                            data.time_zone
                              ? data.time_zones.filter((item) => item.value == data.time_zone)[0]
                              : null
                          }
                          onChange={(e, newTimeZone) => {
                            (data.time_zone = newTimeZone.value), setData({ ...data });
                          }}
                          disableClearable
                          name="time_zone"
                          fullWidth
                          disabled={verifyTimezone}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              inputProps={{
                                ...params.inputProps,
                                autoComplete: 'new-password',
                              }}
                              error={Boolean(
                                timezoneErrs && timezoneErrs.errors && timezoneErrs.errors.time_zone
                              )}
                              label={t('fe_es_identity_verification:time_zone')}
                              variant="outlined"
                              required
                            />
                          )}
                        />
                        {timezoneErrs && (
                          <FormErrors errorMsgs={timezoneErrs} errorField="time_zone" />
                        )}
                      </Grid>
                      <Grid item md={12}>
                        {verifyTimezone && (
                          <Typography
                            className={classes.verify}
                            variant="body2"
                            color="textSecondary"
                          >
                            <VerifiedUserIcon color="primary" />
                            <span>{t('fe_es_identity_verification:verified_time_zone')}</span>
                          </Typography>
                        )}
                      </Grid>
                    </Grid>
                  </Wraper>
                )}
                <Button
                  onClick={continueBtnAtion}
                  variant="contained"
                  color="primary"
                  data-testid="es-checkin-complete-account-continue"
                >
                  {t('fe_es_identity_verification:continue')}
                </Button>
              </Container>
            )}
          </CardContent>
        </Card>
      </Box>
    </>
  );
};
const Wraper = (props) => {
  const classes = useStyles();

  return (
    <Paper className={classes.paper} variant="outlined" elevation={3}>
      <Hidden mdDown>
        <Box className={classes.count}>
          <span className={classes.num} />
        </Box>
      </Hidden>
      <Box className={classes.countentWraper}>
        <Typography component="h5" variant="h4">
          <Hidden lgUp>
            <span className={`${classes.num} ${classes.numMobile}`} />
          </Hidden>
          {props.title}
        </Typography>
        <Box py={3}>{props.children}</Box>
      </Box>
    </Paper>
  );
};

export default withTranslation(['fe_es_identity_verification'])(IdentityVerification);
